import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../api';
import { EventCard } from '../components/events/EventCard';
import { ErrorState, Loading, Notice, PageIntro } from '../components/ui';
import {
  DEFAULT_EVENTS_STATE,
  EVENTS_PAGE_SIZE,
  buildEventsQuery,
  eventsStateToSearch,
  fetchEvents,
  hasEventFilters,
  parseEventsState,
  withEventsChange,
  type EventCoverage,
  type EventLocalityFilter,
  type EventOccurrence,
  type EventPresetResolved,
  type EventsResponse,
  type EventsUrlState,
} from '../eventsApi';
import { addDays, formatDateTime, formatLocalDay, formatLocalDayRange, formatLocalDayShort, formatTime, localToday, plural } from '../format';
import '../components/events/events.css';

/**
 * Events: the local agenda.
 *
 * Ordered by the event's own date and start (server side), grouped by local
 * date. Range, filters and page live in the URL and every change pushes a
 * history entry, so Back steps through them. Saved events stay visible when a
 * source's latest fetch failed; the banner says so instead of showing an
 * empty calendar.
 */

const PRESET_LABELS: Array<{ id: EventPresetResolved; label: string }> = [
  { id: 'today', label: 'Heute' },
  { id: 'weekend', label: 'Dieses Wochenende' },
  { id: 'upcoming', label: 'Demnächst' },
  { id: 'custom', label: 'Zeitraum' },
];

const LOCALITY_OPTIONS: Array<{ id: EventLocalityFilter; label: string }> = [
  { id: 'basel-region', label: 'Basel & Region' },
  { id: 'other', label: 'Andere Orte' },
  { id: 'all', label: 'Alle' },
];

export function EventsPage() {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => parseEventsState(params), [params]);
  const key = useMemo(() => buildEventsQuery(state), [state]);
  const result = useEvents(key, state);

  const update = (change: Partial<EventsUrlState>) => {
    setParams(eventsStateToSearch(withEventsChange(state, change), params));
  };
  const reset = () => setParams(eventsStateToSearch(DEFAULT_EVENTS_STATE, params));
  const data = result.data;

  return (
    <>
      <PageIntro eyebrow="Agenda" title="Was läuft wann?">
        Konzerte und Veranstaltungen nach ihrem eigenen Datum, nicht nach Meldungsalter. Standard ist Basel und Region.
      </PageIntro>

      <EventFilters state={state} data={data} update={update} reset={reset} />

      {data && <CoverageBanner coverage={data.coverage} />}

      <section aria-label="Termine" className="results">
        <div className="results-head">
          <p role="status" aria-live="polite" className="result-count">
            {result.loading && !data ? 'Termine werden geladen…' : data ? countLine(data) : ''}
          </p>
        </div>

        {result.error && <EventsError error={result.error} reset={reset} retry={result.retry} />}
        {result.loading && !data && !result.error && <Loading label="Termine werden geladen…" />}
        {data && !result.error && data.results.length === 0 && <EmptyAgenda data={data} state={state} update={update} reset={reset} />}
        {data && !result.error && data.results.length > 0 && (
          <div className={result.loading ? 'is-refreshing' : undefined} aria-busy={result.loading}>
            <Agenda items={data.results} />
            <Pager data={data} state={state} update={update} />
          </div>
        )}
      </section>
    </>
  );
}

export default EventsPage;

// ---------------------------------------------------------------- data

/** Fetches with cancellation, so a slow earlier response never overwrites a newer one. */
function useEvents(key: string, state: EventsUrlState) {
  const [data, setData] = useState<EventsResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    fetchEvents(state, { signal: ctrl.signal })
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setData(null);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
    // `key` is the serialised state; state itself is re-created per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);
  return { data, error, loading, retry: () => setNonce((n) => n + 1) };
}

function countLine(d: EventsResponse): string {
  const range = formatLocalDayRange(d.from, d.to);
  const place = LOCALITY_OPTIONS.find((l) => l.id === d.locality)?.label ?? d.locality;
  return `${plural(d.total, 'Termin', 'Termine')} · ${range} · ${place} · Stand ${formatTime(d.as_of)}`;
}

// ---------------------------------------------------------------- filters

function EventFilters({
  state,
  data,
  update,
  reset,
}: {
  state: EventsUrlState;
  data: EventsResponse | null;
  update: (c: Partial<EventsUrlState>) => void;
  reset: () => void;
}) {
  const today = localToday();
  const localityCounts = new Map((data?.facets.localities ?? []).map((l) => [l.id, l.count]));
  const localityCount = (id: EventLocalityFilter): number | undefined => {
    if (!data || data.facets.localities.length === 0) return undefined;
    if (id === 'all') return data.facets.localities.reduce((n, l) => n + l.count, 0);
    return localityCounts.get(id) ?? 0;
  };
  const sources = mergeFacet(data?.facets.sources ?? [], state.source);
  const categories = mergeFacet(data?.facets.categories ?? [], state.category);

  return (
    <div className="filters panel" role="group" aria-label="Agenda-Filter">
      <div className="filter-row">
        <span className="field-label" id="ev-range-label">
          Zeitraum
        </span>
        <div className="chip-toggles" role="group" aria-labelledby="ev-range-label">
          {PRESET_LABELS.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={state.preset === p.id}
              onClick={() => {
                if (p.id === state.preset) return;
                if (p.id === 'custom') {
                  // Start from the range on screen, so switching loses nothing.
                  const from = data?.from ?? today;
                  update({ preset: 'custom', from, to: data?.to && data.to >= from ? data.to : addDays(from, 30) });
                } else update({ preset: p.id, from: '', to: '' });
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button type="button" className="text-button reset" onClick={reset}>
          Filter zurücksetzen
        </button>
      </div>

      {state.preset === 'custom' && (
        <div className="filter-row">
          <label className="field-label" htmlFor="ev-from">
            Von
          </label>
          <input id="ev-from" type="date" value={state.from} max={state.to || undefined} onChange={(e) => update({ from: e.target.value })} />
          <label className="field-label" htmlFor="ev-to">
            Bis
          </label>
          <input id="ev-to" type="date" value={state.to} min={state.from || undefined} onChange={(e) => update({ to: e.target.value })} />
          <span className="muted small">Datum der Veranstaltung, Enddatum eingeschlossen.</span>
        </div>
      )}

      <div className="filter-row">
        <span className="field-label" id="ev-place-label">
          Ort
        </span>
        <div className="chip-toggles" role="group" aria-labelledby="ev-place-label">
          {LOCALITY_OPTIONS.map((l) => {
            const n = localityCount(l.id);
            return (
              <button key={l.id} type="button" aria-pressed={state.locality === l.id} onClick={() => update({ locality: l.id })}>
                {l.label}
                {n !== undefined && <span className="count"> {n}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {categories.length > 0 && (
        <div className="filter-row">
          <span className="field-label" id="ev-cat-label">
            Kategorie
          </span>
          <div className="chip-toggles" role="group" aria-labelledby="ev-cat-label">
            {categories.map((c) => {
              const on = state.category.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => update({ category: on ? state.category.filter((x) => x !== c.id) : [...state.category, c.id] })}
                >
                  {c.name}
                  {c.count !== null && <span className="count"> {c.count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {sources.length > 0 && (
        <fieldset className="filter-row event-source-filter">
          <legend className="field-label">Quelle</legend>
          <div className="source-checks">
            {sources.map((s) => {
              const on = state.source.includes(s.id);
              return (
                <label key={s.id} className="check">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => update({ source: on ? state.source.filter((x) => x !== s.id) : [...state.source, s.id] })}
                  />
                  {s.name}
                  {s.count !== null && <span className="count">{s.count}</span>}
                </label>
              );
            })}
          </div>
        </fieldset>
      )}
    </div>
  );
}

/** Facet entries plus any selected id the current result no longer lists, so it can be cleared. */
function mergeFacet(facet: Array<{ id: string; name: string; count: number }>, selected: string[]) {
  const out: Array<{ id: string; name: string; count: number | null }> = facet.map((f) => ({ ...f }));
  for (const id of selected) if (!out.some((f) => f.id === id)) out.push({ id, name: id, count: null });
  return out;
}

// ---------------------------------------------------------------- coverage

const COVERAGE_PROBLEMS = new Set(['partial', 'failed', 'never']);

export function coverageMessage(c: EventCoverage): string {
  switch (c.status) {
    case 'failed':
      return `${c.source_name}: letzte Abfrage fehlgeschlagen – gespeicherte Termine werden weiter angezeigt`;
    case 'partial':
      return `${c.source_name}: letzte Abfrage unvollständig${c.warning_count > 0 ? ` (${plural(c.warning_count, 'Warnung', 'Warnungen')})` : ''} – es können Termine fehlen`;
    case 'never':
      return `${c.source_name}: noch nie abgefragt – von dieser Quelle liegen keine Termine vor`;
    default:
      return c.source_name;
  }
}

function CoverageBanner({ coverage }: { coverage: EventCoverage[] }) {
  const problems = coverage.filter((c) => COVERAGE_PROBLEMS.has(c.status));
  if (problems.length === 0) return null;
  return (
    <Notice kind="warn" title="Nicht alle Quellen sind aktuell">
      <ul>
        {problems.map((c) => (
          <li key={c.source_id}>
            {coverageMessage(c)}
            {c.completed_at && <span className="muted"> · Stand {formatDateTime(c.completed_at)}</span>}
            {c.note && <span className="muted"> · {c.note}</span>}
          </li>
        ))}
      </ul>
    </Notice>
  );
}

// ---------------------------------------------------------------- results

function dayHeading(day: string, today: string): string {
  const label = formatLocalDay(day);
  const withYear = day.slice(0, 4) !== today.slice(0, 4) ? `${label} ${day.slice(0, 4)}` : label;
  if (day === today) return `Heute · ${withYear}`;
  if (day === addDays(today, 1)) return `Morgen · ${withYear}`;
  return withYear;
}

/** Results arrive ordered by date and start; consecutive dates become one group. */
export function groupByDay(items: EventOccurrence[]): Array<{ day: string; items: EventOccurrence[] }> {
  const groups: Array<{ day: string; items: EventOccurrence[] }> = [];
  for (const o of items) {
    const last = groups[groups.length - 1];
    if (last && last.day === o.local_date) last.items.push(o);
    else groups.push({ day: o.local_date, items: [o] });
  }
  return groups;
}

function Agenda({ items }: { items: EventOccurrence[] }) {
  const today = localToday();
  return (
    <>
      {groupByDay(items).map((g) => (
        <section key={g.day} className="event-day" aria-labelledby={`ev-day-${g.day}`}>
          <h2 id={`ev-day-${g.day}`} className="result-group event-day-heading">
            {dayHeading(g.day, today)}
          </h2>
          <div className="event-rows">
            {g.items.map((o) => (
              <EventCard key={o.id} occurrence={o} />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function EmptyAgenda({
  data,
  state,
  update,
  reset,
}: {
  data: EventsResponse;
  state: EventsUrlState;
  update: (c: Partial<EventsUrlState>) => void;
  reset: () => void;
}) {
  const elsewhere = state.locality === 'basel-region' ? data.facets.localities.find((l) => l.id === 'other')?.count ?? 0 : 0;
  const filtered = hasEventFilters(state);
  const narrowed = state.source.length > 0 || state.category.length > 0;
  return (
    <div className="state state-empty">
      {narrowed ? (
        <>
          <h2>Keine Termine mit diesen Filtern.</h2>
          <p className="muted">Im gewählten Zeitraum passt nichts zu Quelle oder Kategorie. Entferne einen Filter.</p>
        </>
      ) : (
        <>
          <h2>Keine Termine in diesem Zeitraum.</h2>
          <p className="muted">
            {state.locality === 'basel-region' ? 'In Basel und Region ist' : state.locality === 'other' ? 'An anderen Orten ist' : 'Es ist'} zwischen{' '}
            {formatLocalDayShort(data.from)} und {formatLocalDayShort(data.to)} nichts gespeichert.
          </p>
        </>
      )}
      <div className="button-row">
        {elsewhere > 0 && (
          <button type="button" className="small-button" onClick={() => update({ locality: 'other' })}>
            {plural(elsewhere, 'Termin', 'Termine')} an anderen Orten zeigen
          </button>
        )}
        {state.preset !== 'upcoming' && !narrowed && (
          <button type="button" className="small-button" onClick={() => update({ preset: 'upcoming', from: '', to: '' })}>
            Alle kommenden Termine
          </button>
        )}
        {filtered && (
          <button type="button" className="small-button" onClick={reset}>
            Filter zurücksetzen
          </button>
        )}
      </div>
    </div>
  );
}

function EventsError({ error, reset, retry }: { error: Error; reset: () => void; retry: () => void }) {
  if (error instanceof ApiError && error.status === 400) {
    return (
      <div className="state state-error" role="alert">
        <h2>Dieser Zeitraum oder Filter ist ungültig.</h2>
        <p>{error.message}</p>
        <button type="button" className="small-button" onClick={reset}>
          Agenda zurücksetzen
        </button>
      </div>
    );
  }
  return <ErrorState error={error} onRetry={retry} />;
}

function Pager({ data, state, update }: { data: EventsResponse; state: EventsUrlState; update: (c: Partial<EventsUrlState>) => void }) {
  if (data.next_offset === null && state.offset === 0) return null;
  const prev = state.offset > 0 ? Math.max(0, state.offset - EVENTS_PAGE_SIZE) : null;
  // The first page's as_of pins the result set: no repeats or skips while paging.
  const asOf = state.as_of || data.as_of;
  const toTop = () => document.querySelector('.results')?.scrollIntoView?.({ block: 'start' });
  return (
    <nav className="pager" aria-label="Seiten">
      <button
        type="button"
        className="small-button"
        disabled={prev === null}
        onClick={() => {
          update({ offset: prev ?? 0, as_of: prev ? asOf : '' });
          toTop();
        }}
      >
        ← Vorherige
      </button>
      <span className="muted small">
        {state.offset + 1}–{state.offset + data.results.length} von {data.total}
      </span>
      <button
        type="button"
        className="small-button"
        disabled={data.next_offset === null}
        onClick={() => {
          update({ offset: data.next_offset ?? 0, as_of: asOf });
          toTop();
        }}
      >
        Weitere Termine →
      </button>
    </nav>
  );
}
